import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';

const ClientsContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 100px 20px;
  background-color: #010606;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, #01bf71, transparent);
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, #01bf71, transparent);
  }
`;

const ClientsWrapper = styled.div`
  max-width: 1200px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
`;

const ClientsHeading = styled.h2`
  font-size: 3rem;
  font-weight: 600;
  color: #fff;
  margin-bottom: 1rem;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 2px;
`;

const ClientsSubheading = styled.p`
  font-size: 1.2rem;
  color: #01bf71;
  margin-bottom: 4rem;
  text-align: center;
  max-width: 600px;
`;

const CarouselContainer = styled.div`
  width: 100%;
  max-width: 1000px;
  position: relative;
  overflow: hidden;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.05);
  padding: 60px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media screen and (max-width: 768px) {
    padding: 20px;
    min-height: 350px;
  }

  @media screen and (max-width: 480px) {
    padding: 15px;
    min-height: 300px;
  }
`;

const Slide = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  opacity: ${props => props.active ? '1' : '0'};
  transform: translateX(${props => props.active ? '0' : '100%'});
  transition: all 0.5s ease-in-out;
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  padding: 20px;
  justify-content: center;

  @media screen and (max-width: 768px) {
    padding: 10px;
  }
`;

const Logo = styled.img`
  width: 250px;
  height: 120px;
  object-fit: contain;
  margin-bottom: 40px;
  background: white;
  padding: 15px;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }

  @media screen and (max-width: 768px) {
    width: 200px;
    height: 100px;
    margin-bottom: 20px;
    padding: 10px;
  }

  @media screen and (max-width: 480px) {
    width: 180px;
    height: 90px;
    margin-bottom: 15px;
    padding: 8px;
  }
`;

const Quote = styled.blockquote`
  font-size: 1.2rem;
  color: #fff;
  text-align: center;
  font-style: italic;
  margin: 0;
  line-height: 1.6;
  max-width: 800px;
  position: relative;
  padding: 0 20px;
  margin-bottom: 20px;

  &::before,
  &::after {
    content: '"';
    color: #01bf71;
    font-size: 1.8rem;
    position: absolute;
  }

  &::before {
    left: -10px;
    top: -10px;
  }

  &::after {
    right: -10px;
    bottom: -20px;
  }

  @media screen and (max-width: 768px) {
    font-size: 1rem;
    padding: 0 15px;
  }

  @media screen and (max-width: 480px) {
    font-size: 0.9rem;
    padding: 0 10px;
  }
`;

const Author = styled.p`
  color: #01bf71;
  font-size: 1.1rem;
  margin-top: 30px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const NavigationDots = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 40px;
  gap: 15px;
`;

const Dot = styled.button`
  width: 15px;
  height: 15px;
  border-radius: 50%;
  border: 2px solid #01bf71;
  background: ${props => props.active ? '#01bf71' : 'transparent'};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #01bf71;
    transform: scale(1.2);
  }
`;

const OurClients = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [timer, setTimer] = useState(null);

  const startTimer = useCallback(() => {
    if (timer) {
      clearInterval(timer);
    }
    const newTimer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % clients.length);
    }, 12000);
    setTimer(newTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timer]);

  useEffect(() => {
    startTimer();
    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDotClick = (index) => {
    setCurrentSlide(index);
    startTimer();
  };

  // Enhanced placeholder data with more realistic content
  const clients = [
    {
      logo: require('../../images/kick.com logo.png'),
      quote: "Brokered a multi 6 figure deal between kick.com and an influencer with over 15,000,000 followers",
      author: "Kick.com"
    },
    {
      logo: require('../../images/netflix logo.png'),
      quote: "Their innovative approach to content promotion helped us reach new audiences and drive engagement across multiple platforms.",
      author: "Netflix"
    },
    {
      logo: require('../../images/sonylogo.png'),
      quote: "Their expertise in digital marketing and content strategy has been instrumental in expanding our reach in the entertainment industry.",
      author: "Sony"
    },
    {
      logo: require('../../images/BWW logo.png'),
      quote: "The team's creative approach to social media marketing has helped us connect with our audience in meaningful ways.",
      author: "BWW"
    },
    {
      logo: require('../../images/prizepicks logo.webp'),
      quote: "Minute Growth's strategic marketing campaigns have significantly boosted our user acquisition and brand awareness in the sports betting space.",
      author: "PrizePicks"
    },
    {
      logo: require('../../images/youngla logo.png'),
      quote: "Their strategic approach to fitness industry marketing has helped us reach new heights in brand recognition.",
      author: "YoungLA"
    },
    {
      logo: require('../../images/rumble logo.png'),
      quote: "Their expertise in platform growth and user engagement has been invaluable to our expansion.",
      author: "Rumble"
    },
    {
      logo: require('../../images/BOMEOW.jpg'),
      quote: "Minute team was awesome. They got us a display truck with prime placement right in Times Square.",
      author: "Book of Meow Coin"
    },
    {
      logo: require('../../images/KISHU logo.png'),
      quote: "Being on the One Times Square billboard is not something we ever imagined, but Minute did it and fit our budget well. 10/10 recommend.",
      author: "KISHU Coin"
    },
    {
      logo: require('../../images/mnm logo.png'),
      quote: "Minute connected us with influencers with over 25 million combined followers for our Youtube promo.",
      author: "MNM"
    },
    {
      logo: require('../../images/SAMO COIN.png'),
      quote: "The team's understanding of crypto marketing has helped us build a strong community and increase visibility.",
      author: "SAMO Coin"
    }
  ];

  return (
    <ClientsContainer id="clients">
      <ClientsWrapper>
        <ClientsHeading>Our Clients</ClientsHeading>
        <ClientsSubheading>
          Trusted by industry leaders and innovative startups worldwide
        </ClientsSubheading>
        <CarouselContainer>
          {clients.map((client, index) => (
            <Slide key={index} active={index === currentSlide}>
              <Logo src={client.logo} alt={`${client.author}'s company logo`} />
              <Quote>{client.quote}</Quote>
              <Author>{client.author}</Author>
            </Slide>
          ))}
        </CarouselContainer>
        <NavigationDots>
          {clients.map((_, index) => (
            <Dot
              key={index}
              active={index === currentSlide}
              onClick={() => handleDotClick(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </NavigationDots>
      </ClientsWrapper>
    </ClientsContainer>
  );
};

export default OurClients; 